'use client';

import type React from 'react';

import type { ChargebeeEvent } from '@/types/events';
import { formatEventType } from '@/constant/eventTypes';
import { formatSource } from '@/constant/eventSources';
import { config } from '@/config';
import { explorerAnalytics } from '@/lib/analytics';
import { useExplorer } from '@/hooks/useExplorer';
import { format } from 'date-fns';
import { cn } from '@components/lib/utils';
import { ExternalLink } from 'lucide-react';

interface EventListItemProps {
  event: ChargebeeEvent;
  isSelected: boolean;
  onClick: () => void;
}

/** Extract company and customer name from event content. */
function extractMetadata(content: Record<string, unknown>) {
  const customer = content?.customer as Record<string, unknown> | undefined;
  const company =
    (customer?.company as string) ?? (content?.company as string) ?? null;
  const firstName = customer?.first_name as string | undefined;
  const lastName = customer?.last_name as string | undefined;
  const name =
    firstName || lastName
      ? [firstName, lastName].filter(Boolean).join(' ')
      : null;
  return { company, name };
}

export const EventListItem: React.FC<EventListItemProps> = ({
  event,
  isSelected,
  onClick,
}) => {
  const { siteName } = useExplorer();
  const eventUrl = siteName
    ? `${config.cbProtocol}://${siteName}.${config.cbDomain}/d/events/${event.id}`
    : null;

  const formattedDate = format(
    new Date(event.occurred_at * 1000),
    'dd-MMM-yyyy HH:mm'
  );
  const { company, name } = extractMetadata(event.content);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className={cn(
        'w-full text-left px-4 py-3.5 border-l-2 border-l-transparent bg-white transition-colors hover:bg-gray-50 cursor-pointer',
        isSelected && 'border-l-shade-teal-500 !bg-shade-teal-50'
      )}
    >
      <div className="flex items-start justify-between gap-4">
        {/* Left: Event type + source */}
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 truncate">
            {formatEventType(event.event_type)}
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            Source:{' '}
            <span className="font-medium">{formatSource(event.source)}</span>
          </p>
        </div>

        {/* Right: ID badge, date, company, name */}
        <div className="flex flex-col items-end gap-1 shrink-0">
          {eventUrl ? (
            <a
              href={eventUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.stopPropagation();
                explorerAnalytics.eventExternalLinkClicked({
                  source: 'event_list_item',
                  eventType: event.event_type,
                });
              }}
              className="flex items-center gap-1 bg-green-50 text-green-700 hover:bg-green-100 rounded px-1.5 py-0.5 transition-colors"
            >
              <ExternalLink className="h-2.5 w-2.5" />
              <span className="text-[10px] font-mono">{event.id}</span>
            </a>
          ) : (
            <div className="flex items-center gap-1 bg-gray-50 text-gray-600 rounded px-1.5 py-0.5">
              <span className="text-[10px] font-mono">{event.id}</span>
            </div>
          )}
          <span className="text-[11px] text-gray-500">{formattedDate}</span>
          <div className="flex items-center gap-3 text-[11px] text-gray-500">
            {company && (
              <span>
                Company:{' '}
                <span className="font-medium text-gray-700">{company}</span>
              </span>
            )}
            {name && (
              <span>
                Name: <span className="font-medium text-gray-700">{name}</span>
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
